import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Anchor, Badge, Code, Fieldset, Group, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { SectionCard, spacePx } from "@xplatform/design-system";
import { IconInfoCircle, IconLock } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { FieldError, JSONSchema, ServiceSchema } from "@models/mappingConfig";
import {
  groupFields,
  humanize,
  isUnset,
  keyString,
  parsePath,
  UNSET,
  type FieldDescriptor,
} from "@utils/mappingConfig";
import { CollectionTable } from "./CollectionTable";
import { ValueInput } from "./ValueInput";
import { prettyJson } from "./format";

type Body = Record<string, unknown>;

export interface SchemaFormProps {
  schema: ServiceSchema;
  value: Body;
  /** Baseline of the edit (collections compute `$ops` against it, Undo of a reset restores it). */
  original?: Body;
  onChange?: (next: Body) => void;
  readOnly?: boolean;
  /** Server field errors (422 `field_errors`), matched to fields and collection rows by `path`. */
  fieldErrors?: FieldError[];
  /** Client-side problems that must block saving (invalid JSON, empty/duplicate collection key), by path. */
  onInvalidChange?: (invalid: Record<string, string>) => void;
  /**
   * Tabbed layout: show only this group, without its section title (the tab
   * names it). The other groups stay mounted but hidden, so their edits and
   * validation state survive switching tabs.
   */
  activeGroup?: string;
}

interface FieldContext {
  root: JSONSchema;
  readOnly: boolean;
  errorsFor: (path: string) => string[];
  rowErrorsFor: (path: string) => Record<string, string[]>;
  setInvalid: (path: string, message: string | null) => void;
}

const FieldLabel: React.FC<{ field: FieldDescriptor; extra?: React.ReactNode }> = ({ field, extra }) => (
  <Group gap={spacePx[1]} wrap="nowrap" component="span">
    <span>{field.label}</span>
    {field.description && (
      <Tooltip label={field.description} multiline maw={420} withinPortal portalProps={{ target: "#admin-portal-root" }}>
        <IconInfoCircle size={14} style={{ opacity: 0.6 }} aria-label={field.description} />
      </Tooltip>
    )}
    {extra}
  </Group>
);

const CodeOwnedField: React.FC<{ field: FieldDescriptor; value: unknown }> = ({ field, value }) => {
  const { t } = useTranslation();
  const badge = (
    <Badge size="xs" variant="light" color="gray" leftSection={<IconLock size={10} />} data-testid={`code-owned-${field.path}`}>
      {t("mappingConfig.form.managedInCode")}
    </Badge>
  );
  const textValue = (v: unknown) => (typeof v === "string" ? v : prettyJson(v));
  return (
    <Stack gap={spacePx[1]} data-field={field.path} data-owner="code">
      <Text size="sm" fw={500} component="div"><FieldLabel field={field} extra={badge} /></Text>
      {field.kind === "promptMap" && value && typeof value === "object" ? (
        Object.entries(value as Body).map(([name, text]) => (
          <Textarea key={name} label={name} value={textValue(text)} readOnly autosize minRows={2} maxRows={12} size="xs"
                    styles={{ input: { fontFamily: "var(--xpf-font-mono)" } }} />
        ))
      ) : field.kind === "prompt" || field.kind === "json" || field.kind === "object" ? (
        <Textarea value={value === undefined ? "" : textValue(value)} readOnly autosize minRows={2} maxRows={12} size="xs"
                  styles={{ input: { fontFamily: "var(--xpf-font-mono)" } }} />
      ) : (
        <Code>{value === undefined ? "-" : textValue(value)}</Code>
      )}
    </Stack>
  );
};

const FieldControl: React.FC<{
  field: FieldDescriptor;
  value: unknown;
  original: unknown;
  onChange: (next: unknown) => void;
  ctx: FieldContext;
  /** Only top-level fields can be reset (`{"$unset": true}` addresses a body field). */
  resettable?: boolean;
}> = ({ field, value, original, onChange, ctx, resettable }) => {
  const { t } = useTranslation();
  const setInvalid = ctx.setInvalid;
  const onInvalid = useCallback((message: string | null) => setInvalid(field.path, message), [setInvalid, field.path]);

  if (field.owner === "code") return <CodeOwnedField field={field} value={value} />;

  const errors = ctx.errorsFor(field.path);
  const error = errors.length > 0 ? errors.join(" ") : undefined;
  const canReset = resettable && !ctx.readOnly && (field.hasDefault || field.nullable || !field.required);

  if (isUnset(value)) {
    return (
      <Stack gap={spacePx[1]} data-field={field.path}>
        <Text size="sm" fw={500} component="div"><FieldLabel field={field} /></Text>
        <Group gap="sm">
          <Badge variant="light" color="orange">{t("mappingConfig.form.willReset")}</Badge>
          {!ctx.readOnly && (
            <Anchor component="button" type="button" size="xs" onClick={() => onChange(isUnset(original) ? undefined : original)}>
              {t("mappingConfig.form.undoReset")}
            </Anchor>
          )}
        </Group>
        {error && <Text size="xs" c="red">{error}</Text>}
      </Stack>
    );
  }

  const resetLink = canReset ? (
    <Anchor component="button" type="button" size="xs" onClick={() => onChange(UNSET)} ml="xs">
      {t("mappingConfig.form.resetToDefault")}
    </Anchor>
  ) : null;

  if (field.kind === "object") {
    const objectValue = (value && typeof value === "object" ? value : {}) as Body;
    const objectOriginal = (original && typeof original === "object" ? original : {}) as Body;
    return (
      <Fieldset legend={<FieldLabel field={field} extra={resetLink} />} data-field={field.path} variant="filled">
        <Stack gap="sm">
          {(field.children ?? []).map((child) => (
            <FieldControl
              key={child.path}
              field={child}
              value={objectValue[child.name]}
              original={objectOriginal[child.name]}
              onChange={(next) => onChange({ ...objectValue, [child.name]: next })}
              ctx={ctx}
            />
          ))}
          {error && <Text size="xs" c="red">{error}</Text>}
        </Stack>
      </Fieldset>
    );
  }

  if (field.kind === "collection") {
    const items = (Array.isArray(value) ? value : []) as Body[];
    const baseline = (Array.isArray(original) ? original : items) as Body[];
    return (
      <Stack gap="xs" data-field={field.path}>
        <Text size="sm" fw={500} component="div"><FieldLabel field={field} extra={resetLink} /></Text>
        <CollectionTable
          descriptor={field}
          root={ctx.root}
          value={items}
          original={baseline}
          readOnly={ctx.readOnly}
          onChange={(next) => onChange(next)}
          rowErrors={ctx.rowErrorsFor(field.path)}
          onInvalid={onInvalid}
        />
        {error && <Text size="xs" c="red">{error}</Text>}
      </Stack>
    );
  }

  if (field.kind === "promptMap") {
    const map = (value && typeof value === "object" ? value : {}) as Body;
    return (
      <Stack gap="xs" data-field={field.path}>
        <Text size="sm" fw={500} component="div"><FieldLabel field={field} extra={resetLink} /></Text>
        {Object.keys(map).length === 0 && <Text size="xs" c="dimmed">-</Text>}
        {Object.entries(map).map(([name, text]) => (
          <ValueInput
            key={name}
            descriptor={{ ...field, kind: "prompt", path: `${field.path}.${name}`, nullable: false }}
            label={name}
            value={text}
            readOnly={ctx.readOnly}
            onChange={(next) => onChange({ ...map, [name]: next })}
          />
        ))}
        {error && <Text size="xs" c="red">{error}</Text>}
      </Stack>
    );
  }

  return (
    <div data-field={field.path}>
      <ValueInput
        descriptor={field}
        value={value}
        onChange={onChange}
        readOnly={ctx.readOnly}
        error={error}
        label={<FieldLabel field={field} extra={resetLink} />}
        onInvalid={onInvalid}
      />
    </div>
  );
};

/**
 * The config form of any mapping service, rendered from the service schema:
 * sections by `x-group` in `groups` order, inputs from JSON Schema types and
 * constraints, `x-format` for prompts and JSON, sub-models as nested
 * fieldsets and `x-key` lists as a CollectionTable. `x-owner: code` fields are
 * not rendered (see `groupFields`); `CodeOwnedField` only covers a stray one.
 */
export const SchemaForm: React.FC<SchemaFormProps> = ({
  schema, value, original, onChange, readOnly = false, fieldErrors = [], onInvalidChange, activeGroup,
}) => {
  const { t } = useTranslation();
  const groups = useMemo(() => groupFields(schema), [schema]);
  const baseline = original ?? value;

  const invalidRef = useRef<Record<string, string>>({});
  const onInvalidRef = useRef(onInvalidChange);
  onInvalidRef.current = onInvalidChange;
  const setInvalid = useCallback((path: string, message: string | null) => {
    const current = invalidRef.current;
    if ((current[path] ?? null) === message) return;
    const next = { ...current };
    if (message === null) delete next[path];
    else next[path] = message;
    invalidRef.current = next;
    onInvalidRef.current?.(next);
  }, []);
  useEffect(() => () => {
    invalidRef.current = {};
  }, []);

  const ctx = useMemo<FieldContext>(() => {
    const parsed = fieldErrors.map((e) => ({ ...parsePath(e.path), message: e.message }));
    return {
      root: schema.json_schema,
      readOnly: readOnly || !onChange,
      setInvalid,
      errorsFor: (path) => parsed.filter((p) => p.field === path && p.rowKey === undefined).map((p) => p.message),
      rowErrorsFor: (path) => {
        const out: Record<string, string[]> = {};
        for (const p of parsed) {
          if (p.field !== path || p.rowKey === undefined) continue;
          const k = keyString(p.rowKey);
          (out[k] ??= []).push(p.rowField ? `${p.rowField}: ${p.message}` : p.message);
        }
        return out;
      },
    };
  }, [schema.json_schema, readOnly, onChange, fieldErrors, setInvalid]);

  return (
    <Stack gap="md">
      {groups.map(({ group, fields }) => (
        <SectionCard
          key={group}
          title={activeGroup ? undefined : t(`mappingConfig.groups.${group}`, { defaultValue: humanize(group) })}
          data-group={group}
          // Inline style, not the `hidden` attribute: the card's own display wins over it.
          style={activeGroup !== undefined && activeGroup !== group ? { display: "none" } : undefined}
        >
          <Stack gap="md">
            {fields.map((field) => (
              <FieldControl
                key={field.path}
                field={field}
                value={value[field.name]}
                original={baseline[field.name]}
                onChange={(next) => {
                  const updated = { ...value };
                  if (next === undefined) delete updated[field.name];
                  else updated[field.name] = next;
                  onChange?.(updated);
                }}
                ctx={ctx}
                resettable
              />
            ))}
          </Stack>
        </SectionCard>
      ))}
    </Stack>
  );
};
